import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { NetworkStatusIndicator } from '../shared/OfflineBanner';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { APP_BRANDING } from '../../utils/config';

/**
 * Dashboard Header Component - Clean City Belmopan
 * Mobile-first design with compact header and city council branding
 */
const DashboardHeader = ({ onChangePassword, onContactUs, onShowTerms, onLogout, onShowSettings }) => {
  const { user } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showGearMenu, setShowGearMenu] = useState(false);
  const gearMenuRef = useRef(null);

  // Add PWA install functionality
  const { canInstall, installApp, isInstalled } = usePWAInstall();

  // Add PWA install handler
  const handleInstallClick = async () => {
    await installApp();
  };

  // Close gear menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (gearMenuRef.current && !gearMenuRef.current.contains(event.target)) {
        setShowGearMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleGearMenuClick = (action) => {
    setShowGearMenu(false);
    action();
  };

  return (
    <header className="bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-600 shadow-lg">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Mobile: Compact header h-14, Desktop: h-16 */}
        <div className="flex justify-between items-center h-14 md:h-16">
          {/* Logo/Title with City Council Branding */}
          <div className="flex items-center min-w-0">
            <div className="flex-shrink-0 flex items-center">
              {/* Logo - smaller on mobile */}
              <div className="bg-white rounded-lg p-1 md:p-1.5 mr-2 md:mr-3 shadow-md">
                <img
                  src="/bmpCITCO_64.png"
                  alt="Belmopan City Council"
                  className="w-6 h-6 md:w-7 md:h-7"
                />
              </div>
              <div className="flex flex-col min-w-0">
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-white leading-tight truncate">
                  <span className="hidden sm:inline">{APP_BRANDING.name}</span>
                  <span className="sm:hidden">{APP_BRANDING.shortName}</span>
                </h1>
                <span className="text-[10px] md:text-xs text-blue-100 hidden sm:inline truncate">
                  {APP_BRANDING.tagline}
                </span>
              </div>
            </div>
          </div>

          {/* Desktop User Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex flex-col text-right">
              <span className="text-white text-sm font-medium truncate max-w-[200px]">
                {user?.email}
              </span>
              <span className="text-xs text-blue-200">
                {APP_BRANDING.organization}
              </span>
            </div>
            <NetworkStatusIndicator variant="header" />

            {/* Gear Icon Dropdown Menu */}
            <div className="relative" ref={gearMenuRef}>
              <button
                onClick={() => setShowGearMenu(!showGearMenu)}
                className="bg-white/20 text-white p-2 rounded-md hover:bg-white/30 transition-colors flex items-center justify-center backdrop-blur-sm"
                title="Settings & Options"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showGearMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="py-1">
                    <button
                      onClick={() => handleGearMenuClick(onContactUs)}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 flex items-center transition-colors"
                    >
                      <span className="mr-3">📧</span>
                      Contact Us
                    </button>
                    <button
                      onClick={() => handleGearMenuClick(onChangePassword)}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 flex items-center transition-colors"
                    >
                      <span className="mr-3">🔒</span>
                      Change Password
                    </button>
                    <button
                      onClick={() => handleGearMenuClick(onShowTerms)}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 flex items-center transition-colors"
                    >
                      <span className="mr-3">📋</span>
                      Terms & Conditions
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    <button
                      onClick={() => handleGearMenuClick(onLogout)}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                    >
                      <span className="mr-3">🚪</span>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button and PWA Install */}
          <div className="md:hidden flex items-center space-x-1">
            <NetworkStatusIndicator variant="header" />

            {/* PWA Install Button - Mobile Only */}
            {canInstall && !isInstalled && (
              <button
                onClick={handleInstallClick}
                className="bg-white/20 text-white p-1.5 rounded-md hover:bg-white/30 transition-colors flex items-center justify-center"
                title="Install App"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}

            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="text-white hover:text-blue-100 focus:outline-none transition-colors p-1.5"
            >
              <span className="sr-only">Open main menu</span>
              {showMobileMenu ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-blue-500/30 bg-blue-800/50 backdrop-blur-sm rounded-b-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* User Info */}
              <div className="px-3 py-2 border-b border-blue-500/30">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-white rounded-full p-1.5">
                      <span className="text-lg">👤</span>
                    </div>
                  </div>
                  <div className="ml-3 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{user?.email}</div>
                    <div className="text-xs text-blue-200">{APP_BRANDING.organization}</div>
                  </div>
                </div>
              </div>

              {/* Mobile Action Buttons */}
              <div className="space-y-0.5 pt-1">
                {/* PWA Install Option in Mobile Menu */}
                {canInstall && (
                  <button
                    onClick={() => {
                      handleInstallClick();
                      setShowMobileMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-white/10 flex items-center transition-colors"
                  >
                    <span className="mr-3">📱</span>
                    Install App
                  </button>
                )}

                <button
                  onClick={() => {
                    onContactUs();
                    setShowMobileMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-white/10 flex items-center transition-colors"
                >
                  <span className="mr-3">📧</span>
                  Contact Us
                </button>
                <button
                  onClick={() => {
                    onChangePassword();
                    setShowMobileMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-white/10 flex items-center transition-colors"
                >
                  <span className="mr-3">🔒</span>
                  Change Password
                </button>
                <button
                  onClick={() => {
                    onShowTerms();
                    setShowMobileMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-white/10 flex items-center transition-colors"
                >
                  <span className="mr-3">📋</span>
                  Terms & Conditions
                </button>
                <div className="border-t border-blue-500/30 my-1"></div>
                <button
                  onClick={() => {
                    onLogout();
                    setShowMobileMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-red-300 hover:bg-red-500/20 flex items-center transition-colors"
                >
                  <span className="mr-3">🚪</span>
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default DashboardHeader;
